import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ClipboardCheck, X } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { VerbChip, type SubmittalVerb } from '@/components/ui/verb-chip';
import {
  useDecideProposalMutation,
  useReviewProposalQuery,
  type DecideVerb,
  type RoutingProposal,
} from '@/lib/api/review-proposals';
import { cn } from '@/lib/utils/cn';

/**
 * T-0115 / E10 — Submittal review-state modal.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-review-state.js`
 * → `openReviewState(recordKey)` + `design_handoff_caliber_v2/README.md`
 * §4 modal `data-set-review`, §6 (VerbChip primitive).
 *
 * What this modal does:
 *   - Inspects an AI proposal's review-state lane and lets the
 *     consultant set or change the submittals verb (Approved / Approved
 *     as Noted / Revise / Rejected / Outstanding / Overdue / Submitted).
 *   - Verb commits via the existing T-0045 review-decide endpoint
 *     (POST /review/proposals/:id/decide).
 *   - The decision rationale is required for noted / revise / rejected
 *     (matches prototype `STATE_DETAILS`).
 *   - Pending / accepted / edited / rejected lanes are shown as inline
 *     pills next to the verb chip.
 *
 * Why a thin wrapper over T-0045: the underlying decide endpoint is
 * already proven. This modal is the verb-picker surface for a single
 * row's review state — the bulk review pane stays where it is on the
 * proposals queue route.
 *
 * Verb→backend decision map (matches T-0045):
 *   Approved          → 'approved'
 *   Approved as Noted → 'approved_as_noted'   (rationale required)
 *   Revise & Resubmit → 'revise'              (rationale required)
 *   Rejected          → 'rejected'            (rationale required)
 *   Outstanding/Overdue/Submitted → not commit-able via this surface;
 *     they're lifecycle states the deliverable transitions service owns
 *     (T-0065). Surfaced as "current" pills only.
 */

export interface ReviewStateModalProps {
  engagementId: string;
  /** AI proposal id whose review-state we're setting. */
  proposalId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface VerbOption {
  verb: SubmittalVerb;
  decision: DecideVerb | null;
  description: string;
  rationaleRequired: boolean;
}

const VERB_OPTIONS: VerbOption[] = [
  {
    verb: 'approved',
    decision: 'approved',
    description: 'Item enters the contract baseline as proposed.',
    rationaleRequired: false,
  },
  {
    verb: 'approved_as_noted',
    decision: 'approved_as_noted',
    description: 'Item enters the baseline with noted conditions in rationale.',
    rationaleRequired: true,
  },
  {
    verb: 'revise',
    decision: 'revise',
    description: 'Returned to AI with specific revisions; will re-extract.',
    rationaleRequired: true,
  },
  {
    verb: 'rejected',
    decision: 'rejected',
    description: 'Removed from candidate baseline; never enters EDL.',
    rationaleRequired: true,
  },
  {
    verb: 'submitted',
    decision: null,
    description: 'Lifecycle state owned by the deliverable transitions service (T-0065).',
    rationaleRequired: false,
  },
  {
    verb: 'outstanding',
    decision: null,
    description: 'Lifecycle state owned by the deliverable transitions service (T-0065).',
    rationaleRequired: false,
  },
  {
    verb: 'overdue',
    decision: null,
    description: 'Auto-computed by the overdue scan once expected_delivery_date has passed.',
    rationaleRequired: false,
  },
];

export function ReviewStateModal({
  engagementId,
  proposalId,
  open,
  onOpenChange,
}: ReviewStateModalProps) {
  // T-0122 ships GET /review/proposals/:id — a single-hop fetch keyed by
  // the proposal id. Previously we pulled the entire queue and filtered
  // client-side; that worked for tens of rows but scaled poorly. The
  // single-hop endpoint is RLS-scoped by engagement membership.
  const proposalQuery = useReviewProposalQuery(
    open ? engagementId : undefined,
    open ? proposalId : undefined,
  );
  const proposal = proposalQuery.data;

  const decide = useDecideProposalMutation(engagementId);

  const [selectedVerb, setSelectedVerb] = useState<SubmittalVerb | null>(null);
  const [rationale, setRationale] = useState('');
  const [saveError, setSaveError] = useState<unknown>(null);

  // Initialize the selected verb from the existing decision when the
  // modal opens with a fresh proposal.
  useEffect(() => {
    if (!open) {
      setSelectedVerb(null);
      setRationale('');
      setSaveError(null);
      return;
    }
    if (proposal && selectedVerb === null) {
      setSelectedVerb(decisionToVerb(proposal.decision));
      setRationale(proposal.decision_rationale ?? '');
    }
  }, [open, proposal, selectedVerb]);

  const selectedOption = VERB_OPTIONS.find((o) => o.verb === selectedVerb) ?? null;
  const canSave =
    selectedOption !== null &&
    selectedOption.decision !== null &&
    (!selectedOption.rationaleRequired || rationale.trim().length > 0);

  async function save() {
    if (!selectedOption?.decision) return;
    setSaveError(null);
    try {
      await decide.mutateAsync({
        proposalId,
        input: {
          verb: selectedOption.decision,
          ...(selectedOption.rationaleRequired ? { decision_rationale: rationale.trim() } : {}),
        },
        idempotencyKey:
          typeof globalThis.crypto?.randomUUID === 'function'
            ? globalThis.crypto.randomUUID()
            : `review-${proposalId}-${Date.now()}`,
      });
      onOpenChange(false);
    } catch (err) {
      setSaveError(err);
    }
  }

  const titleId = `review-state-modal-title-${proposalId}`;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(720px,92vw)] max-h-[88vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-ink-100 px-6 py-4 pr-14">
            <div className="min-w-0 flex items-start gap-3">
              <div
                aria-hidden="true"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-card bg-ai-surface text-ai-ink"
              >
                <ClipboardCheck className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-meta uppercase tracking-wide text-ink-500">
                  Baseline review
                </div>
                <Dialog.Title
                  id={titleId}
                  className="mt-1 truncate text-h2 font-semibold text-ink-900"
                >
                  {proposal ? formatProposalName(proposal) : 'Set submittal verb'}
                </Dialog.Title>
                {proposal ? (
                  <div className="mt-1 flex flex-wrap items-baseline gap-2 text-meta text-ink-500">
                    <span className="font-mono">{proposal.id.slice(0, 8)}…</span>
                    <span>·</span>
                    <span>confidence {proposal.confidence.toFixed(2)}</span>
                    <span>·</span>
                    <CurrentLane decision={proposal.decision} />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="overflow-y-auto px-6 py-5">
            {proposalQuery.isLoading ? (
              <div className="flex items-center gap-2 text-body-sm text-ink-500">
                <Spinner /> Loading review state…
              </div>
            ) : proposalQuery.error ? (
              <ErrorMessage error={proposalQuery.error} />
            ) : !proposal ? (
              <p className="text-body-sm text-ink-500">Proposal not found.</p>
            ) : (
              <div className="space-y-5">
                <div>
                  <div className="text-meta uppercase tracking-wide text-ink-500">
                    Set submittal verb
                  </div>
                  <div className="mt-2 grid gap-2">
                    {VERB_OPTIONS.map((opt) => {
                      const selected = selectedVerb === opt.verb;
                      const disabled = opt.decision === null;
                      return (
                        <button
                          key={opt.verb}
                          type="button"
                          disabled={disabled}
                          onClick={() => setSelectedVerb(opt.verb)}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-card border px-3 py-2.5 text-left transition-colors',
                            selected
                              ? 'border-ink-900 bg-paper-50'
                              : 'border-ink-100 bg-paper-0 hover:border-ink-200 hover:bg-paper-50',
                            disabled && 'cursor-not-allowed opacity-50 hover:bg-paper-0',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                          )}
                          aria-pressed={selected}
                        >
                          <span
                            aria-hidden="true"
                            className={cn(
                              'inline-flex h-4 w-4 flex-none items-center justify-center rounded-full border',
                              selected ? 'border-ink-900 bg-ink-900' : 'border-ink-200',
                            )}
                          >
                            {selected ? (
                              <span className="h-1.5 w-1.5 rounded-full bg-paper-0" />
                            ) : null}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-body-sm font-medium text-ink-900">
                              {VERB_LABEL[opt.verb]}
                            </div>
                            <div className="text-meta text-ink-500">{opt.description}</div>
                          </div>
                          <VerbChip verb={opt.verb} />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {selectedOption?.decision !== null ? (
                  <div>
                    <label
                      htmlFor="review-rationale"
                      className="text-meta uppercase tracking-wide text-ink-500"
                    >
                      Rationale
                      {selectedOption?.rationaleRequired ? (
                        <span className="ml-1 text-posture-critical">· required</span>
                      ) : (
                        <span className="ml-1 font-normal text-ink-500">· optional</span>
                      )}
                    </label>
                    <textarea
                      id="review-rationale"
                      value={rationale}
                      onChange={(e) => setRationale(e.target.value)}
                      placeholder="e.g. Approved as noted — payment trigger needs Schedule C cross-reference fix before commit."
                      className={cn(
                        'mt-2 block min-h-[100px] w-full rounded-card border border-ink-200 bg-paper-0 px-3 py-2 text-body-sm text-ink-900',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                      )}
                    />
                  </div>
                ) : null}

                {saveError ? <ErrorMessage error={saveError} /> : null}

                {proposal ? <ProposalCitations proposal={proposal} /> : null}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
            <div className="text-meta text-ink-500">
              Audit-logged with actor + timestamp + before/after.
            </div>
            <div className="flex items-center gap-2">
              <Dialog.Close
                className={cn(
                  'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
                  'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                )}
              >
                Cancel
              </Dialog.Close>
              <button
                type="button"
                onClick={save}
                disabled={!canSave || decide.isPending}
                className={cn(
                  'inline-flex h-9 items-center gap-1.5 rounded-card bg-ink-900 px-3 text-body-sm font-medium text-paper-0',
                  'hover:bg-ink-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                {decide.isPending ? <Spinner /> : null}
                <span>Save</span>
              </button>
            </div>
          </div>

          <Dialog.Close
            aria-label="Close review state"
            className={cn(
              'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-card',
              'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const VERB_LABEL: Record<SubmittalVerb, string> = {
  approved: 'Approved',
  approved_as_noted: 'Approved as Noted',
  revise: 'Revise & Resubmit',
  rejected: 'Rejected',
  outstanding: 'Outstanding',
  overdue: 'Overdue',
  submitted: 'Submitted',
};

function decisionToVerb(d: RoutingProposal['decision']): SubmittalVerb | null {
  switch (d) {
    case 'approved':
      return 'approved';
    case 'approved_as_noted':
      return 'approved_as_noted';
    case 'revise':
      return 'revise';
    case 'rejected':
      return 'rejected';
    case 'pending':
    default:
      return null;
  }
}

function CurrentLane({ decision }: { decision: RoutingProposal['decision'] }) {
  const v = decisionToVerb(decision);
  if (!v) {
    return (
      <span className="rounded-full bg-ink-100 px-2 py-0.5 text-meta text-ink-700">
        Pending review
      </span>
    );
  }
  return <VerbChip verb={v} />;
}

function ProposalCitations({ proposal }: { proposal: RoutingProposal }) {
  if (proposal.citations.length === 0) {
    return null;
  }
  return (
    <section className="rounded-card border border-ink-100 bg-paper-0 p-3">
      <div className="text-meta uppercase tracking-wide text-ink-500">Citations</div>
      <ul className="mt-2 space-y-1.5">
        {proposal.citations.map((c) => (
          <li key={c.id} className="flex flex-wrap items-baseline gap-1.5 text-body-sm">
            <span className="font-medium text-ink-900">{c.document_title}</span>
            {c.section_path ? (
              <span className="font-mono text-meta text-ink-500">§{c.section_path}</span>
            ) : null}
            {typeof c.page_number === 'number' ? (
              <span className="font-mono text-meta text-ink-500">p.{c.page_number}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function formatProposalName(proposal: RoutingProposal): string {
  const targetRef = proposal.target_ref;
  if (targetRef && typeof targetRef === 'object') {
    const ref = targetRef as Record<string, unknown>;
    if (typeof ref.entity_label === 'string') return ref.entity_label;
    if (typeof ref.title === 'string') return ref.title;
    if (typeof ref.register_entry_id === 'string') {
      return `Register entry ${(ref.register_entry_id as string).slice(0, 8)}…`;
    }
  }
  return `${proposal.proposal_type} proposal`;
}
